import {
  DataStatus,
  DropdownGroup,
  DropdownItem,
  FormControl,
  Label,
  openDropdown,
  showNotification,
} from 'platform/components';
import {HStack, Link, VStack} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';

import {
  ContextTemplateResponseBody,
  useGetContextTemplateListQuery,
  useRenderBusinessCaseEmailTemplateMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {myProfileRoutes} from '@dms/routes';

import {Nullish} from 'shared/web';

import {useEmailBodyImageUpload} from '../../../hooks/useEmailBodyImageUpload';
import {useTemplatesByLocale} from '../../../hooks/useTemplatesByLocale';
import {VARIABLE_REGEX} from '../../../utils/getVariables';
import {templateBodyToEmailHtml} from '../../../utils/templateBodyToEmailHtml';
import {EmailBodyFormField} from '../../EmailBodyFormField/EmailBodyFormField';
import {FormValues} from '../types/SendOnlineOfferEmailFormSchema';

interface MessageFormFieldProps {
  formApi: UseFormReturn<FormValues>;
  control: FormControl<FormValues>;
  // this is BC id for now
  recordId: string;
  preferredLocale?: string | Nullish;
  upload: ReturnType<typeof useEmailBodyImageUpload>;
}

export function MessageFormField(props: MessageFormFieldProps) {
  const {
    data: templates,
    isLoading: isGetContextTemplateListQueryLoading,
    isError: isTemplatesError,
  } = useGetContextTemplateListQuery({
    type: 'email',
    context: 'business-case',
  });

  const [renderBusinessCaseTemplate, {isLoading: isRenderBusinessCaseTemplateInProgress}] =
    useRenderBusinessCaseEmailTemplateMutation();

  const isLoadingTemplates =
    isGetContextTemplateListQueryLoading || isRenderBusinessCaseTemplateInProgress;

  const onTemplateClick = (template: ContextTemplateResponseBody) => {
    renderBusinessCaseTemplate({
      renderBusinessCaseEmailTemplateRequestBody: {
        template: {
          subject: template.subject ?? '',
          body: template.body,
        },
        businessCaseId: props.recordId,
      },
    })
      .unwrap()
      .then((response) => {
        props.formApi.setValue('body', templateBodyToEmailHtml(response.body), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        if (VARIABLE_REGEX.test(response.body)) {
          showNotification.warning(i18n.t('general.notifications.notAllPlaceholdersFilled'));
        }
      });
  };

  const templatesByLocale = useTemplatesByLocale(templates, props.preferredLocale);

  return (
    <VStack spacing={1}>
      <EmailBodyFormField
        control={props.control}
        name="body"
        labelActions={[
          {
            title: i18n.t('entity.customer.labels.chooseEmailTemplate'),
            leftIcon: 'action/autorenew',
            isDisabled: isLoadingTemplates,
            onClick: (event) =>
              openDropdown(
                event,
                <>
                  <DataStatus
                    isError={isTemplatesError}
                    isLoading={isGetContextTemplateListQueryLoading}
                  />
                  {templatesByLocale.isGrouped
                    ? templatesByLocale.groups.map((group) => (
                        <DropdownGroup key={group.key} label={group.label}>
                          {group.templates.map((template) => (
                            <DropdownItem
                              label={template.name}
                              key={template.id}
                              onClick={() => onTemplateClick(template)}
                            />
                          ))}
                        </DropdownGroup>
                      ))
                    : templatesByLocale.templates.map((template) => (
                        <DropdownItem
                          label={template.name}
                          key={template.id}
                          onClick={() => onTemplateClick(template)}
                        />
                      ))}
                </>,
                {
                  isHeightLimited: true,
                }
              ),
          },
        ]}
        upload={props.upload}
        isRequired
        data-testid="sendOnlineOfferEmail"
      />
      <HStack justify="space-between" width="100%">
        <Label>{i18n.t('entity.customer.labels.messageWithSignature')}</Label>
        <Link
          leftIcon="image/edit"
          size="xSmall"
          href={myProfileRoutes.emailSignature}
          target="_blank"
        >
          {i18n.t('entity.customer.labels.goToUserSignature')}
        </Link>
      </HStack>
    </VStack>
  );
}
